package dev.kuml.portal.web.activity

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ActivityDayResponse
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.error.ErrorCopy
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.html.Div
import io.kvision.html.div
import kotlinx.coroutines.launch

/**
 * R-15: the user's own KI-Diagramm-Generierungs-Aktivitaet on the project-list home screen, in the
 * shape of a GitHub-style contribution calendar. "Aktivitaet" is EXCLUSIVELY the count of AI
 * diagram generations this account made (bindende Nutzer-Entscheidung dieser Welle) -- logins or
 * project edits are not a logged event anywhere in this codebase and are deliberately NOT part of
 * this calendar.
 *
 * Rendered as nested KVision `Div`s (CSS Grid columns/rows), NEVER as an inline-SVG string built
 * via `innerHTML` -- this module's rule (`niemals innerHTML fuer dynamische/serverseitige
 * Inhalte`) applies here exactly like everywhere else in `kuml-portal-web`, and every container
 * below is populated purely through KVision's own DSL (`div {}`), never `content =`/`content +=`
 * mixed with child-widget calls in the same container (this module's other hard rule).
 *
 * A load failure here renders only a small status line inside this section -- it must NEVER hide
 * or replace the project grid above it (that is the actual primary content of this screen).
 */
public fun Container.activitySection(api: PortalApi) {
    lateinit var headSummary: Div
    lateinit var monthsRow: Div
    lateinit var gridHost: Div
    lateinit var legendHost: Div
    lateinit var statusDiv: Div

    div(className = "kportal-activity") {
        div(className = "kportal-activity-head") {
            div(t.activity.sectionTitle, className = "kportal-activity-title")
            headSummary = div("", className = "kportal-activity-summary")
        }
        monthsRow = div(className = "kportal-activity-months")
        gridHost = div(className = "kportal-activity-grid")
        legendHost = div(className = "kportal-activity-legend")
        statusDiv = div(className = "kportal-status-area")
    }

    fun renderSkeleton() {
        gridHost.removeAll()
        gridHost.div(t.activity.loading, className = "kportal-status-neutral")
    }

    fun render(days: List<ActivityDayResponse>) {
        val weeks = ActivityCalendar.weeks(days)
        val monthLabels = ActivityCalendar.monthLabels(weeks, t.format)
        val maxCount = days.maxOfOrNull { it.count } ?: 0L

        monthsRow.removeAll()
        weeks.forEachIndexed { index, _ ->
            monthsRow.div(monthLabels[index] ?: "", className = "kportal-activity-month-label")
        }

        gridHost.removeAll()
        for (week in weeks) {
            gridHost.div(className = "kportal-activity-week") {
                for (day in week) {
                    val level = ActivityCalendar.level(day.count, maxCount)
                    val cell = div(className = "kportal-activity-day level-$level")
                    cell.setAttribute("title", ActivityCalendar.tooltip(day, t.activity, t.format))
                }
            }
        }

        legendHost.removeAll()
        legendHost.div(t.activity.legendLess, className = "kportal-activity-legend-label")
        for (level in 0 until ActivityCalendar.LEVELS) {
            legendHost.div(className = "kportal-activity-legend-swatch level-$level")
        }
        legendHost.div(t.activity.legendMore, className = "kportal-activity-legend-label")
    }

    fun reload() {
        renderSkeleton()
        statusDiv.content = ""
        AppScope.launch {
            when (val result = api.activity()) {
                is ApiResult.Success -> {
                    val activity = result.value
                    headSummary.content = t.activity.summary(activity.totalGenerations, activity.currentStreakDays.toLong())
                    render(activity.days)
                }
                is ApiResult.HttpError -> {
                    gridHost.removeAll()
                    val copy =
                        ErrorCopy.forHttpStatus(
                            status = result.status,
                            serverCode = result.error?.code,
                            serverMessage = result.error?.message,
                            retryAfterSeconds = result.retryAfterSeconds,
                            aiLimit = null,
                            resetLabel = null,
                            strings = t.errors,
                        )
                    statusDiv.content = copy.message
                }
                ApiResult.NetworkError -> {
                    gridHost.removeAll()
                    statusDiv.content = t.activity.loadFailed
                }
            }
        }
    }

    reload()
}
